import type { PLPFilterOption } from "@/features/products/ProductListingPage/types";

export const isNumeric = (value: string) => value !== "" && !Number.isNaN(Number(value));

export const isDashedSize = (value: string) => {
	const parts = value.split("-");
	return parts.length === 2 && isNumeric(parts[0]) && isNumeric(parts[1]);
};

// Matches "5y", "5-6y", "11-12y" — leading digits, optional dashed range, trailing y/Y.
const CHILDREN_SIZE_RE = /^(\d+)(?:-\d+)?y$/i;
// Matches "3m", "6m", "9-12m" — baby/infant sizes in months. Treated as children
// and sorted before year-based sizes (3m < 1y).
const CHILDREN_MONTH_SIZE_RE = /^(\d+)(?:-\d+)?m$/i;

export const isChildrenSize = (value: string) =>
	CHILDREN_SIZE_RE.test(value) || CHILDREN_MONTH_SIZE_RE.test(value);

const childrenAgeStart = (value: string): number => {
	const monthMatch = value.match(CHILDREN_MONTH_SIZE_RE);
	if (monthMatch) return Number(monthMatch[1]) / 12;
	const yearMatch = value.match(CHILDREN_SIZE_RE);
	return yearMatch ? Number(yearMatch[1]) : Number.POSITIVE_INFINITY;
};

const LETTER_ORDER = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const letterRank = (value: string) => {
	const idx = LETTER_ORDER.indexOf(value.toUpperCase());
	return idx === -1 ? Number.POSITIVE_INFINITY : idx;
};

export type SizeCategory = "adult" | "children" | "other";

export const classifySize = (value: string): SizeCategory => {
	if (isChildrenSize(value)) return "children";
	if (isNumeric(value) || isDashedSize(value)) return "adult";
	if (letterRank(value) !== Number.POSITIVE_INFINITY) return "adult";
	// Compound letter sizes like "M/L", "XS/S" — anchor on the first segment.
	const firstSeg = value.split("/")[0];
	if (letterRank(firstSeg) !== Number.POSITIVE_INFINITY) return "adult";
	return "other";
};

const compoundLetterRank = (value: string): number => {
	const direct = letterRank(value);
	if (direct !== Number.POSITIVE_INFINITY) return direct;
	const firstSeg = value.split("/")[0];
	const segRank = letterRank(firstSeg);
	// Compound (e.g. "M/L") sorts just after its anchor letter.
	return segRank === Number.POSITIVE_INFINITY ? segRank : segRank + 0.5;
};

const compareWithinCategory = (a: string, b: string, category: SizeCategory): number => {
	if (category === "children") return childrenAgeStart(a) - childrenAgeStart(b);
	if (category === "adult") {
		if (isNumeric(a) && isNumeric(b)) return Number(a) - Number(b);
		if (isDashedSize(a) && !isDashedSize(b)) return 1;
		if (!isDashedSize(a) && isDashedSize(b)) return -1;
		return compoundLetterRank(a) - compoundLetterRank(b);
	}
	return 0;
};

const CATEGORY_ORDER: Record<SizeCategory, number> = { adult: 0, children: 1, other: 2 };

export const sortSizes = (options: PLPFilterOption[]): PLPFilterOption[] => {
	const copy = [...options];
	copy.sort((a, b) => {
		const aCat = classifySize(a.value);
		const bCat = classifySize(b.value);
		if (aCat !== bCat) return CATEGORY_ORDER[aCat] - CATEGORY_ORDER[bCat];
		return compareWithinCategory(a.value, b.value, aCat);
	});
	return copy;
};

export const groupSizeOptions = (
	options: PLPFilterOption[],
): Array<{ category: SizeCategory; options: PLPFilterOption[] }> => {
	const buckets: Record<SizeCategory, PLPFilterOption[]> = { adult: [], children: [], other: [] };
	for (const opt of options) buckets[classifySize(opt.value)].push(opt);

	// "One Size" and other uncategorized entries roll into adult — they have
	// no natural home in the children bucket and a dedicated OTHER heading
	// just for them is visual noise.
	const adultMerged = [...buckets.adult, ...buckets.other];
	const hasChildren = buckets.children.length > 0;

	// When there are no children sizes, return a single group so the consumer
	// (SizeToggleGrid) skips the ADULT heading entirely. Otherwise return
	// both groups so the headings disambiguate adult vs. children.
	if (!hasChildren) {
		return adultMerged.length > 0 ? [{ category: "adult", options: adultMerged }] : [];
	}
	const groups: Array<{ category: SizeCategory; options: PLPFilterOption[] }> = [];
	if (adultMerged.length > 0) groups.push({ category: "adult", options: adultMerged });
	groups.push({ category: "children", options: buckets.children });
	return groups;
};
